import { useFlags } from 'config/useFlagsContext'
import {
  useAcompanhamentoCidadaosVulnerabilidadeAlimentarQuery,
  useAcompanhamentoVulnerabilidadeCardQuery,
  useFolhaRostoVulnerabilidadeFamiliarQuery,
  useIsBeneficiarioBolsaFamiliaQuery,
} from 'graphql/hooks.generated'
import { TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import { isUndefinedOrNull } from 'util/checks'

import { AcompanhamentoVulnerabilidadeContent } from './AcompanhamentoVulnerabilidadeContent'
import { isCidadaoVulneravelAlimentar } from './util-acompanhamentoVulnerabilidade'

interface AcompanhamentoVulnerabilidadeSectionProps {
  cidadaoId: ID
  dataReferencia: Instant
  tipoAcompanhamento?: TipoAcompanhamentoEnum
  showModalButton?: boolean
}

export function AcompanhamentoVulnerabilidadeSection(props: AcompanhamentoVulnerabilidadeSectionProps) {
  const { cidadaoId, tipoAcompanhamento, showModalButton } = props
  const { PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED } = useFlags()

  const { data: cardData, loading: cardLoading } = useAcompanhamentoVulnerabilidadeCardQuery({
    variables: { cidadaoId },
    context: { newRequest: true },
  })

  const cidadaosFamiliaId = useMemo(
    () =>
      cardData?.cidadao?.familias?.flatMap((familia) => familia?.cidadaos?.map((cidadao) => cidadao?.id) ?? []) ?? [],
    [cardData]
  )

  const { data: cidadaosData, loading: cidadaosLoading } = useAcompanhamentoCidadaosVulnerabilidadeAlimentarQuery({
    variables: { input: { idCidadaoList: cidadaosFamiliaId } },
    context: { newRequest: true },
    skip: cidadaosFamiliaId.isEmpty(),
  })

  const { data: bolsaFamiliaData, loading: bolsaFamiliaLoading } = useIsBeneficiarioBolsaFamiliaQuery({
    variables: { cidadaoId },
    context: { newRequest: true },
  })

  const { data: vulnerabilidadeData, loading: vulnerabilidadeLoading } = useFolhaRostoVulnerabilidadeFamiliarQuery({
    variables: { input: { idCidadaoList: [cidadaoId] } },
    context: { newRequest: true },
    skip: !PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED,
  })

  const familiarVulneravel = useMemo(
    () => cidadaosData?.cidadaos?.content?.find((familiar) => isCidadaoVulneravelAlimentar(familiar)),
    [cidadaosData]
  )

  const isBeneficiarioBolsaFamilia = bolsaFamiliaData?.cidadao?.beneficiarioBolsaFamilia?.isBeneficiario ?? false

  const familiaAlvo = useMemo(() => {
    const familias = vulnerabilidadeData?.cidadaos?.content?.flatMap((cidadao) => cidadao?.familias ?? []) ?? []
    return familias.find((familia) => !familia?.imovel?.possuiInconsistencia && familia?.vulnerabilidadeFamiliar)
  }, [vulnerabilidadeData])

  const vulnerabilidade = familiaAlvo?.vulnerabilidadeFamiliar

  const loading = cardLoading || cidadaosLoading || bolsaFamiliaLoading || vulnerabilidadeLoading

  const hasVulnerabilidadeFamiliar = !isUndefinedOrNull(vulnerabilidade)
  const hasBolsaFamiliaOuRiscoAlimentar = isBeneficiarioBolsaFamilia || !isUndefinedOrNull(familiarVulneravel)
  const hasEscoreVulnerabilidade = PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED && hasVulnerabilidadeFamiliar
  const showAcompanhamentoVulnerabilidade = hasBolsaFamiliaOuRiscoAlimentar || hasEscoreVulnerabilidade

  return (
    <AcompanhamentoVulnerabilidadeContent
      tipoAcompanhamento={tipoAcompanhamento}
      loading={loading}
      showAcompanhamentoVulnerabilidade={showAcompanhamentoVulnerabilidade}
      isBeneficiarioBolsaFamilia={isBeneficiarioBolsaFamilia}
      dataRegistro={familiarVulneravel?.informacoesSocioEconomicasDW?.dataRegistro}
      vulnerabilidade={vulnerabilidade}
      showModalButton={showModalButton}
    />
  )
}
